import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ThreeDots } from "react-loader-spinner";

import { Dialog, DialogContent, DialogActions, Chip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { client } from "../../../services/axiosClient";
import Button from "../../../components/CustomButtons/Button.js";
import ModalHeader from "../../../components/ModalHeader";
import FilterDialog from "../../../components/FilterDialog/FilterDialog";
import useThemeColors from "../../../hooks/useThemeColors";

const useStyles = makeStyles(() => ({
  dimRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 0",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
  },
  dimName: {
    flex: "1 1 auto",
    fontSize: "13px",
    fontWeight: 500,
  },
  dimValues: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    maxWidth: "55%",
  },
  empty: {
    color: "#888",
    fontSize: "13px",
    fontStyle: "italic",
    padding: "12px 0",
  },
  spinnerWrap: { display: "flex", justifyContent: "center", padding: "30px" },
}));

/**
 * Editor de data scope (restriction_filters) de um usuario.
 *
 * Carrega as dimensoes disponiveis via api/data_explorer_meta/ (mesmo
 * metadado do DataExplorer / Downloads) e usa o FilterDialog para
 * selecionar valores por dimensao. Constroi o objeto {dim: [valores]}.
 */
export default function ScopeDialog({ open, onClose, user, onSave }) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { clientColor } = useThemeColors();

  const [loading, setLoading] = useState(true);
  // dimensions: [{ key, label, items: [string] }]
  const [dimensions, setDimensions] = useState([]);
  // working filters: { dim: [values] }
  const [working, setWorking] = useState({});

  // FilterDialog state
  const [activeDim, setActiveDim] = useState(null);
  const [dialogSelected, setDialogSelected] = useState([]);

  useEffect(() => {
    if (!open) return;
    setWorking(user?.restriction_filters || {});
    setLoading(true);
    client
      .post("api/data_explorer_meta/")
      .then((response) => {
        const f = response.data.filters || {};
        const list = (f.list || []).filter((d) => d !== "period");
        const dims = list.map((key) => ({
          key,
          label: f.names?.[key]?.[i18n.language] || key,
          items: f.items?.[key] || [],
        }));
        setDimensions(dims);
        setLoading(false);
      })
      .catch(() => {
        setDimensions([]);
        setLoading(false);
      });
  }, [open, user, i18n.language]);

  const activeDimObj = useMemo(
    () => dimensions.find((d) => d.key === activeDim) || null,
    [dimensions, activeDim],
  );

  const allItemsForDialog = useMemo(() => {
    if (!activeDimObj) return [];
    return activeDimObj.items.map((display, index) => ({
      display,
      originalIndex: index,
    }));
  }, [activeDimObj]);

  const openDimensionPicker = (dim) => {
    const dimObj = dimensions.find((d) => d.key === dim);
    if (!dimObj) return;
    const currentValues = working[dim] || [];
    const indices = currentValues
      .map((v) => dimObj.items.indexOf(v))
      .filter((idx) => idx >= 0);
    setDialogSelected(indices);
    setActiveDim(dim);
  };

  const applyDimensionSelection = () => {
    if (!activeDimObj) return;
    const values = dialogSelected
      .map((idx) => activeDimObj.items[idx])
      .filter((v) => v != null);
    setWorking((prev) => {
      const next = { ...prev };
      if (values.length === 0) {
        delete next[activeDimObj.key];
      } else {
        next[activeDimObj.key] = values;
      }
      return next;
    });
    setActiveDim(null);
  };

  const clearDimension = (dim) => {
    setWorking((prev) => {
      const next = { ...prev };
      delete next[dim];
      return next;
    });
  };

  const handleSave = () => {
    onSave(working);
  };

  const hasAnyFilter = Object.keys(working).length > 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <ModalHeader
          title={t("access_management.scope.title", {
            name: user?.name || "",
          })}
          onClose={onClose}
        />
        <DialogContent>
          {loading ? (
            <div className={classes.spinnerWrap}>
              <ThreeDots color={clientColor} height={40} width={40} />
            </div>
          ) : (
            <>
              {!hasAnyFilter && (
                <div className={classes.empty}>
                  {t("access_management.scope.no_restriction")}
                </div>
              )}
              {dimensions.map((dim) => {
                const values = working[dim.key] || [];
                return (
                  <div key={dim.key} className={classes.dimRow}>
                    <span className={classes.dimName}>{dim.label}</span>
                    <div className={classes.dimValues}>
                      {values.slice(0, 4).map((v) => (
                        <Chip key={v} label={v} size="small" />
                      ))}
                      {values.length > 4 && (
                        <Chip label={`+${values.length - 4}`} size="small" />
                      )}
                    </div>
                    <Button
                      simple
                      color="client"
                      size="sm"
                      onClick={() => openDimensionPicker(dim.key)}
                    >
                      {values.length > 0
                        ? t("access_management.scope.edit_dim")
                        : t("access_management.scope.add_dim")}
                    </Button>
                    {values.length > 0 && (
                      <Button
                        simple
                        color="danger"
                        size="sm"
                        onClick={() => clearDimension(dim.key)}
                      >
                        {t("access_management.scope.clear_dim")}
                      </Button>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="gray" simple>
            {t("access_management.cancel")}
          </Button>
          <Button onClick={handleSave} color="client">
            {t("access_management.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reuso do FilterDialog (modo items apenas, sem grupos/ranges) */}
      <FilterDialog
        open={Boolean(activeDim)}
        onClose={() => setActiveDim(null)}
        headerTitle={activeDimObj?.label || ""}
        headerScope={t("access_management.scope.header_scope")}
        selectedItems={dialogSelected}
        onItemsSelect={(indexes) => setDialogSelected(indexes)}
        onSelectNone={() => setDialogSelected([])}
        onClearFilter={() => setDialogSelected([])}
        onApply={applyDimensionSelection}
        groupChoices={[]}
        groupSelected=""
        onGroupSelect={() => {}}
        filterMode={dialogSelected.length > 0 ? "items" : ""}
        clientColor={clientColor}
        allItems={allItemsForDialog}
      />
    </>
  );
}

ScopeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  onSave: PropTypes.func.isRequired,
};
